/**
 * SimpleModeOverlay — Task-first guided workflows on top of the live WebGIS map.
 * Keeps AppShell + map mounted so measure/draw/import events always work.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import UploadFile from '@mui/icons-material/UploadFile';
import Edit from '@mui/icons-material/Edit';
import Straighten from '@mui/icons-material/Straighten';
import ThreeDRotation from '@mui/icons-material/ThreeDRotation';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import AddLocation from '@mui/icons-material/AddLocation';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Tune from '@mui/icons-material/Tune';
import Key from '@mui/icons-material/Key';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Share from '@mui/icons-material/Share';
import Badge from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import RateReviewIcon from '@mui/icons-material/RateReview';
import GpsFixed from '@mui/icons-material/GpsFixed';
import Architecture from '@mui/icons-material/Architecture';
import Terrain from '@mui/icons-material/Terrain';
import PhoneAndroid from '@mui/icons-material/PhoneAndroid';
import TableChart from '@mui/icons-material/TableChart';
import Public from '@mui/icons-material/Public';
import Calculate from '@mui/icons-material/Calculate';
import { useNavigate } from 'react-router-dom';
import {
  isWorkTemplateId,
  WORK_TEMPLATE_IDS,
  WORK_TEMPLATES,
  LEGACY_SIMPLE_MODE_PERSONA_STORAGE_KEY,
  SIMPLE_MODE_STARTED_TASKS_STORAGE_KEY,
  SIMPLE_MODE_TEMPLATE_STORAGE_KEY,
  WORKFLOW_TASK_IDS,
  WORKFLOW_TASKS,
  type WorkTemplateId,
  type WorkflowIconId,
  type WorkflowTaskDefinition,
  type WorkflowTaskId,
} from '../config/simpleModeWorkflows';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  addNotification,
  endTaskWizard,
  setNonTechnicalMode,
  startTaskWizard,
} from '../store/slices/uiSlice';
import { setMeasurementMode } from '../store/slices/measurementSlice';
import { setActiveTool as setDrawingTool } from '../store/slices/drawingSlice';
import { useGeoveraTheme } from '../theme/ThemeContext';
import FeedbackModal from './FeedbackModal';
import OnboardingTour from './OnboardingTour';
import PropertyCardWizard from './propertyCard/PropertyCardWizard';
import ShareMapDialog from './ShareMapDialog';
import DrawBoundaryWizard from './wizards/DrawBoundaryWizard';
import ReportWizard from './wizards/ReportWizard';

const TASK_ICONS: Record<WorkflowIconId, React.ReactNode> = {
  upload: <UploadFile sx={{ fontSize: 24 }} />,
  boundary: <Edit sx={{ fontSize: 24 }} />,
  measure: <Straighten sx={{ fontSize: 24 }} />,
  'three-d': <ThreeDRotation sx={{ fontSize: 24 }} />,
  report: <PictureAsPdf sx={{ fontSize: 24 }} />,
  points: <AddLocation sx={{ fontSize: 24 }} />,
  property: <Badge sx={{ fontSize: 24 }} />,
  share: <Share sx={{ fontSize: 24 }} />,
  cloud: <CloudUpload sx={{ fontSize: 24 }} />,
  survey: <GpsFixed sx={{ fontSize: 24 }} />,
  design: <Architecture sx={{ fontSize: 24 }} />,
  terrain: <Terrain sx={{ fontSize: 24 }} />,
  field: <PhoneAndroid sx={{ fontSize: 24 }} />,
  attributes: <TableChart sx={{ fontSize: 24 }} />,
  crs: <Public sx={{ fontSize: 24 }} />,
  analysis: <Calculate sx={{ fontSize: 24 }} />,
};

const getInitialWorkTemplate = (): WorkTemplateId => {
  if (typeof window === 'undefined') return 'custom';
  try {
    const savedTemplate =
      localStorage.getItem(SIMPLE_MODE_TEMPLATE_STORAGE_KEY) ??
      localStorage.getItem(LEGACY_SIMPLE_MODE_PERSONA_STORAGE_KEY);
    if (isWorkTemplateId(savedTemplate)) return savedTemplate;
  } catch {
    // Storage may be unavailable in privacy-restricted browsers.
  }
  return 'custom';
};

const getInitialStartedTaskIds = (): WorkflowTaskId[] => {
  if (typeof window === 'undefined') return [];
  try {
    const savedTasks = JSON.parse(
      localStorage.getItem(SIMPLE_MODE_STARTED_TASKS_STORAGE_KEY) ?? '[]'
    ) as unknown;
    if (!Array.isArray(savedTasks)) return [];
    return savedTasks.filter(
      (taskId): taskId is WorkflowTaskId =>
        typeof taskId === 'string' && WORKFLOW_TASK_IDS.includes(taskId as WorkflowTaskId)
    );
  } catch {
    return [];
  }
};

interface SimpleModeOverlayProps {
  /** Called when user wants full tools without leaving map */
  onRequestAdvanced?: () => void;
  /** Hide the guide panel (map stays; parent can show reopen chip) */
  onHide?: () => void;
}

const SimpleModeOverlay: React.FC<SimpleModeOverlayProps> = ({ onRequestAdvanced, onHide }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { palette } = useGeoveraTheme();
  const isNonTechnical = useAppSelector((s) => s.ui.isNonTechnicalMode);

  const [selectedWorkTemplateId, setSelectedWorkTemplateId] =
    useState<WorkTemplateId>(getInitialWorkTemplate);
  const [startedTaskIds, setStartedTaskIds] = useState<WorkflowTaskId[]>(getInitialStartedTaskIds);
  const [drawWizardOpen, setDrawWizardOpen] = useState(false);
  const [reportWizardOpen, setReportWizardOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [propertyCardOpen, setPropertyCardOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [allToolsOpen, setAllToolsOpen] = useState(false);

  const selectedTemplate = WORK_TEMPLATES[selectedWorkTemplateId];
  const isCustomTemplate = selectedWorkTemplateId === 'custom';

  useEffect(() => {
    if (!isNonTechnical) return;

    let hasSeenTour = false;
    try {
      hasSeenTour = localStorage.getItem('geovera_seen_onboarding') === 'true';
    } catch {
      // Continue without persistence when storage is unavailable.
    }

    if (!hasSeenTour) {
      const timer = window.setTimeout(() => {
        setTourOpen(true);
        try {
          localStorage.setItem('geovera_seen_onboarding', 'true');
        } catch {
          // The tour still works when storage is unavailable.
        }
      }, 600);
      return () => window.clearTimeout(timer);
    }
  }, [isNonTechnical]);

  const handleTemplateChange = (
    _event: React.MouseEvent<HTMLElement>,
    templateId: WorkTemplateId | null
  ) => {
    if (!templateId) return;
    setSelectedWorkTemplateId(templateId);
    setAllToolsOpen(false);
    try {
      localStorage.setItem(SIMPLE_MODE_TEMPLATE_STORAGE_KEY, templateId);
    } catch {
      // Keep the in-memory selection when storage is unavailable.
    }
  };

  const freeMapForTask = useCallback(() => {
    setCollapsed(true);
    // Hide the guide panel so map clicks are not blocked under the cards.
    onHide?.();
  }, [onHide]);

  const runTask = useCallback(
    (taskId: WorkflowTaskId) => {
      const task = WORKFLOW_TASKS[taskId];
      const { action } = task;
      setStartedTaskIds((currentTaskIds) => {
        if (currentTaskIds.includes(taskId)) return currentTaskIds;
        const nextTaskIds = [...currentTaskIds, taskId];
        try {
          localStorage.setItem(SIMPLE_MODE_STARTED_TASKS_STORAGE_KEY, JSON.stringify(nextTaskIds));
        } catch {
          // Keep session progress when storage is unavailable.
        }
        return nextTaskIds;
      });
      dispatch(startTaskWizard(taskId));

      switch (action.kind) {
        case 'dialog':
          switch (action.dialog) {
            case 'draw-boundary':
              setDrawWizardOpen(true);
              return;
            case 'generate-report':
              setReportWizardOpen(true);
              return;
            case 'property-card':
              setPropertyCardOpen(true);
              dispatch(endTaskWizard());
              return;
            case 'share-map':
              setShareDialogOpen(true);
              dispatch(endTaskWizard());
              return;
          }
          return;

        case 'drawing':
          dispatch(setDrawingTool(action.geometry));
          dispatch(setMeasurementMode(null));
          dispatch(
            addNotification({
              type: 'info',
              message: 'Point mode is ready — click the map to mark each place.',
            })
          );
          dispatch(endTaskWizard());
          freeMapForTask();
          return;

        case 'measurement':
          dispatch(setDrawingTool(null));
          dispatch(setMeasurementMode(action.mode));
          dispatch(
            addNotification({
              type: 'info',
              message:
                'Measurement is ready — click the map and use the bottom bar to switch type.',
            })
          );
          dispatch(endTaskWizard());
          freeMapForTask();
          return;

        case 'event':
          window.dispatchEvent(new CustomEvent(action.eventName, { detail: action.detail }));
          if (taskId === 'view-3d') {
            dispatch(
              addNotification({ type: 'info', message: '3D preview opened below the map.' })
            );
          }
          dispatch(endTaskWizard());
          freeMapForTask();
          return;

        case 'activity': {
          dispatch(endTaskWizard());
          dispatch(setNonTechnicalMode(false));
          onRequestAdvanced?.();
          const openRequestedActivity = () => {
            window.dispatchEvent(
              new CustomEvent('geovera:open-activity', { detail: { id: action.activityId } })
            );
          };
          // Wait until AppShell's Simple → Advanced effect has restored its panel state.
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(openRequestedActivity);
          });
          dispatch(
            addNotification({
              type: 'info',
              message: `${task.title} opened in the professional workspace.`,
            })
          );
          return;
        }
      }
    },
    [dispatch, freeMapForTask, onRequestAdvanced]
  );

  const closeWizards = () => {
    setDrawWizardOpen(false);
    setReportWizardOpen(false);
    setTourOpen(false);
    dispatch(endTaskWizard());
  };

  const switchToAdvanced = () => {
    dispatch(setNonTechnicalMode(false));
    onRequestAdvanced?.();
    dispatch(
      addNotification({
        type: 'info',
        message: 'Advanced mode — full WebGIS tools',
      })
    );
  };

  const renderTaskCard = (task: WorkflowTaskDefinition, step?: number) => {
    const taskColor = palette.accent[task.tone];
    const opensSpecialistPanel = task.action.kind === 'activity';
    const wasStarted = startedTaskIds.includes(task.id);

    return (
      <Grid size={{ xs: 12 }} key={task.id}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${palette.border.subtle}`,
            bgcolor: palette.bg.surface,
            transition: 'border-color 150ms ease',
            '&:hover': { borderColor: taskColor },
          }}
        >
          <CardContent sx={{ display: 'flex', gap: 1.25, pb: 0.75, pt: 1.25, px: 1.25 }}>
            <Avatar
              sx={{
                bgcolor: alpha(taskColor, 0.14),
                color: taskColor,
                width: 38,
                height: 38,
                flexShrink: 0,
              }}
            >
              {TASK_ICONS[task.icon]}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {step !== undefined && (
                  <Chip size="small" label={`Step ${step}`} sx={{ fontWeight: 700 }} />
                )}
                {wasStarted && (
                  <Chip size="small" label="Started" color="success" sx={{ fontWeight: 700 }} />
                )}
                {opensSpecialistPanel && (
                  <Chip
                    size="small"
                    label="Professional panel"
                    variant="outlined"
                    color={task.tone}
                    sx={{ fontWeight: 700 }}
                  />
                )}
              </Stack>
              <Typography sx={{ fontWeight: 750, fontSize: '0.9rem', mt: 0.5 }}>
                {task.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: palette.text.secondary, lineHeight: 1.4, display: 'block', mt: 0.25 }}
              >
                {task.description}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: palette.text.muted, lineHeight: 1.4, display: 'block', mt: 0.5 }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: palette.text.secondary }}>
                  Result:{' '}
                </Box>
                {task.outcomeHint}
              </Typography>
            </Box>
          </CardContent>
          <CardActions sx={{ px: 1.25, pb: 1.25, pt: 0.25 }}>
            <Button
              size="small"
              variant="contained"
              color={task.tone}
              fullWidth
              onClick={() => runTask(task.id)}
              sx={{ fontWeight: 700 }}
            >
              {task.actionLabel}
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  if (!isNonTechnical) return null;

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'absolute',
          left: { xs: 8, sm: 12 },
          top: { xs: 8, sm: 12 },
          zIndex: 40,
          width: { xs: 'calc(100% - 16px)', sm: 380, md: 420 },
          maxHeight: { xs: '72vh', sm: '82vh' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: alpha(palette.bg.elevated, 0.96),
          border: `1px solid ${palette.border.base}`,
          borderRadius: 2,
          backdropFilter: 'blur(12px)',
          // Only this panel captures pointer events — map stays interactive around it.
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${palette.border.subtle}`,
          }}
        >
          <Chip label="Simple mode" color="success" size="small" sx={{ fontWeight: 700 }} />
          <Typography sx={{ flex: 1, fontWeight: 800, fontSize: '0.95rem' }}>
            Guided workspace
          </Typography>
          <IconButton
            size="small"
            onClick={() => setFeedbackOpen(true)}
            aria-label="Give feedback"
            title="Give feedback"
            sx={{ color: palette.accent.secondary }}
          >
            <RateReviewIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setCollapsed((current) => !current)}
            aria-label={collapsed ? 'Expand guide' : 'Collapse guide'}
          >
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onHide?.()}
            aria-label="Hide guide"
            title="Hide guide (map stays open)"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Collapse in={!collapsed} sx={{ minHeight: 0 }}>
          <Box
            sx={{
              p: 1.5,
              overflow: 'auto',
              maxHeight: { xs: 'calc(72vh - 52px)', sm: 'calc(82vh - 52px)' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
              Choose a starting point (optional)
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: palette.text.muted, display: 'block', mb: 0.75 }}
            >
              These are example workflows, not a fixed list of professions. Choose “Build my own”
              for any other kind of project.
            </Typography>
            <Box sx={{ overflowX: 'auto', pb: 0.5, mb: 1 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={selectedWorkTemplateId}
                onChange={handleTemplateChange}
                aria-label="Choose an optional starter workflow"
                sx={{
                  minWidth: 'max-content',
                  '& .MuiToggleButton-root': {
                    px: 1.25,
                    py: 0.6,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: palette.text.secondary,
                    borderColor: palette.border.base,
                  },
                  '& .Mui-selected': {
                    color: palette.accent.primary,
                    bgcolor: `${palette.accent.primaryDim} !important`,
                  },
                }}
              >
                {WORK_TEMPLATE_IDS.map((templateId) => (
                  <ToggleButton key={templateId} value={templateId}>
                    {WORK_TEMPLATES[templateId].title}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                mb: 1.25,
                bgcolor: palette.accent.primaryDim,
                border: `1px solid ${alpha(palette.accent.primary, 0.35)}`,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                {selectedTemplate.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: palette.text.secondary, lineHeight: 1.45, display: 'block', mt: 0.35 }}
              >
                {selectedTemplate.purpose}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: palette.text.primary, lineHeight: 1.45, display: 'block', mt: 0.6 }}
              >
                <Box component="span" sx={{ fontWeight: 800 }}>
                  What this gives you:{' '}
                </Box>
                {selectedTemplate.outcome}
              </Typography>
            </Paper>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<Tune />}
                onClick={switchToAdvanced}
                sx={{ fontWeight: 700, borderRadius: 999 }}
              >
                Advanced mode
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Key />}
                onClick={() => navigate('/developers/digipin')}
                sx={{ fontWeight: 700, borderRadius: 999 }}
              >
                API key
              </Button>
              <Button
                size="small"
                startIcon={<PlayArrow />}
                onClick={() => setTourOpen(true)}
                sx={{ fontWeight: 600, borderRadius: 999 }}
              >
                Tour
              </Button>
            </Stack>

            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 0.25 }}>
              {isCustomTemplate ? 'What do you need to do?' : 'Suggested steps'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: palette.text.muted, display: 'block', mb: 1 }}
            >
              {isCustomTemplate
                ? 'Choose any task in any order. The platform does not require a profession or preset process.'
                : 'Use these example steps in order, skip them, or open any other tool below.'}
            </Typography>
            <Grid container spacing={1}>
              {selectedTemplate.taskIds.map((taskId, index) =>
                renderTaskCard(WORKFLOW_TASKS[taskId], isCustomTemplate ? undefined : index + 1)
              )}
            </Grid>

            {!isCustomTemplate && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  endIcon={allToolsOpen ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => setAllToolsOpen((current) => !current)}
                  aria-expanded={allToolsOpen}
                  sx={{ justifyContent: 'space-between', fontWeight: 700 }}
                >
                  All tools ({WORKFLOW_TASK_IDS.length})
                </Button>
                <Typography
                  variant="caption"
                  sx={{ color: palette.text.muted, display: 'block', mt: 0.65 }}
                >
                  Starter templates only arrange suggestions. Every tool remains available to every
                  user and every kind of project.
                </Typography>
                <Collapse in={allToolsOpen}>
                  <Grid container spacing={1} sx={{ mt: 0.25 }}>
                    {WORKFLOW_TASK_IDS.map((taskId) => renderTaskCard(WORKFLOW_TASKS[taskId]))}
                  </Grid>
                </Collapse>
              </>
            )}
          </Box>
        </Collapse>
      </Paper>

      <DrawBoundaryWizard open={drawWizardOpen} onClose={closeWizards} />
      <ReportWizard open={reportWizardOpen} onClose={closeWizards} />
      <ShareMapDialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} />
      <PropertyCardWizard open={propertyCardOpen} onClose={() => setPropertyCardOpen(false)} />
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </>
  );
};

export default SimpleModeOverlay;
